import { useCallback, useEffect, useState } from 'react'

import type { AdminStatsResponse } from '@study-decks/shared'

import { useAuth } from '../../auth/AuthContext.js'
import { Button } from '../../components'
import { AdminApiError, getAdminStats } from '../../lib/admin-api.js'
import styles from './StatsPage.module.css'

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

interface MetricCardProps {
  value: number
  label: string
  tooltip: string
}

function MetricCard({ value, label, tooltip }: MetricCardProps): JSX.Element {
  const [showTooltip, setShowTooltip] = useState(false)

  return (
    <div className={styles.metricCard}>
      <span className={styles.metricValue}>{value.toLocaleString()}</span>
      <span className={styles.metricName}>
        {label}
        <span className={styles.tooltip}>
          <button
            type="button"
            className={styles.tooltipTrigger}
            aria-label={`About ${label}`}
            onClick={() => {
              setShowTooltip((v) => !v)
            }}
            onBlur={() => {
              setShowTooltip(false)
            }}
          >
            ?
          </button>
          {showTooltip ? (
            <span className={styles.tooltipContent} role="tooltip">
              {tooltip}
            </span>
          ) : null}
        </span>
      </span>
    </div>
  )
}

export default function StatsPage(): JSX.Element {
  const { firebaseUser } = useAuth()
  const [stats, setStats] = useState<AdminStatsResponse | null>(null)
  const [cached, setCached] = useState(false)
  const [generatedAt, setGeneratedAt] = useState<Date | null>(null)
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setStatus('Loading…')
    try {
      const getIdToken = () => firebaseUser?.getIdToken() ?? Promise.resolve('')
      const result = await getAdminStats(getIdToken)
      setStats(result.stats)
      setCached(result.cached)
      setGeneratedAt(new Date(result.stats.generatedAtIso))
      setStatus('Updated just now')
    } catch (err) {
      const msg = err instanceof AdminApiError ? err.message : "Couldn't load stats — try again."
      setStatus(msg)
    } finally {
      setLoading(false)
    }
  }, [firebaseUser])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <section className={styles.page} aria-labelledby="stats-title">
      <div>
        <p className={styles.eyebrow}>Admin</p>
        <h2 id="stats-title">Site Stats</h2>
        <p className={styles.intro}>
          Aggregate usage metrics. No personally identifiable information is shown.
        </p>
      </div>

      <p role="status" aria-live="polite" aria-atomic="true">
        {status}
      </p>

      {!loading && stats ? (
        <>
          <div className={styles.metricsGrid}>
            <MetricCard
              value={stats.dau}
              label="DAU"
              tooltip="Daily active users: distinct users who logged in the last 24 hours."
            />
            <MetricCard
              value={stats.wau}
              label="WAU"
              tooltip="Weekly active users: distinct users who logged in the last 7 days."
            />
            <MetricCard
              value={stats.activeDeckCount}
              label="Active decks"
              tooltip="Decks with published status visible to students."
            />
            <MetricCard
              value={stats.studySessions7d}
              label="Sessions (7d)"
              tooltip="Study sessions completed in the last 7 days across all modes."
            />
          </div>

          <div className={styles.domainsSection}>
            <h3>Top domains</h3>
            {stats.topDomains.length > 0 ? (
              <div className={styles.tableWrap}>
                <table className={styles.domainsTable} aria-label="Top domains by user count">
                  <thead>
                    <tr>
                      <th scope="col">Domain</th>
                      <th scope="col">Users</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.topDomains.map((row) => (
                      <tr key={row.domain}>
                        <td>{row.domain}</td>
                        <td>{row.userCount.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No domain data yet.</p>
            )}
          </div>
        </>
      ) : null}

      <div className={styles.footer}>
        <Button
          type="button"
          variant="secondary"
          loading={loading}
          onClick={() => {
            void load()
          }}
        >
          Refresh
        </Button>
        <p className={styles.cacheNote}>
          {cached && generatedAt
            ? `Cache: generated at ${DATE_FORMATTER.format(generatedAt)} (up to 5 min old)`
            : generatedAt
              ? `Fetched fresh at ${DATE_FORMATTER.format(generatedAt)}`
              : ''}
        </p>
      </div>
    </section>
  )
}
